/**
 * CompanyDirectory — presentation-only, company-grouped view for /plants
 * and /directory.
 *
 * Groups published plant records by normalized legal/company name
 * (prefers the backfilled `legal_entity_key`; falls back to a client-side
 * normalization that mirrors backend corridor_reconcile.legal_entity_key).
 *
 * ZERO API calls, ZERO writes. It renders whatever `items` are handed in
 * (the same GET /api/manufacturers payload the pages already load).
 * No record is merged, renamed, hidden, or modified — grouping is visual.
 *
 * Truth rules enforced here:
 *   - Company-level badges appear ONLY when every included plant qualifies.
 *   - Every plant row keeps its own Gov-listed / Claimed / Corridor Verified /
 *     Legacy / Demo-Test / Review Candidate state.
 *   - Nothing is invented: no capabilities, contacts, capacity, or activity.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, X, ChevronDown, ChevronRight, MapPin, Building2, ArrowRight,
} from "lucide-react";
import { PlantCard } from "./PlantCard";
import {
  GovListedBadge,
  PendingVerificationBadge,
  EvidenceBackedBadge,
  CorridorVerifiedBadge,
  LegacyVerifiedBadge,
  ClaimedBadge,
  UnclaimedBadge,
  DemoBadge,
  ReviewCandidateBadge,
  DUPLICATE_CANDIDATE_SLUGS,
} from "../pages/adminUiHelpers";

/* ── Normalization (mirrors backend corridor_reconcile) ─────────────── */
const ACCENTS = { Á: "A", É: "E", Í: "I", Ó: "O", Ú: "U", Ü: "U", Ñ: "N" };
const SUFFIX_RE = /\b(S\s*A\s*DE\s*C\s*V|S\s*DE\s*R\s*L\s*DE\s*C\s*V|SAPI\s*DE\s*C\s*V|S\s*C|S\s*A|SRL|SA|SDERL)\b/g;

function normKey(name) {
  if (!name) return "";
  let s = String(name).toUpperCase();
  s = s.replace(/[ÁÉÍÓÚÜÑ]/g, (ch) => ACCENTS[ch] || ch);
  s = s.replace(/[^A-Z0-9 ]/g, " ");
  s = s.replace(SUFFIX_RE, " ");
  return s.replace(/\s+/g, " ").trim();
}

function resolveName(m) {
  for (const c of [m.display_name, m.company_name, m.legal_name, m.legal_entity]) {
    if (c && String(c).trim()) return String(c).trim();
  }
  return m.slug || "—";
}

function seqNum(m) {
  const n = m.plant_sequence_for_program;
  return Number.isFinite(n) ? n : 999;
}

function domKey(key) {
  return key.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "x";
}

/* ── Grouping ───────────────────────────────────────────────────────── */
export function groupCompanies(items) {
  const map = new Map();
  for (const m of items) {
    const key =
      m.legal_entity_key ||
      normKey(m.display_name || m.company_name || m.legal_name || m.legal_entity) ||
      `__${m.slug}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(m);
  }
  const groups = [];
  for (const [key, plants] of map.entries()) {
    plants.sort((a, b) => seqNum(a) - seqNum(b) || (a.slug || "").localeCompare(b.slug || ""));
    const cities = [...new Set(plants.map((p) => [p.city, p.state].filter(Boolean).join(", ")).filter(Boolean))];
    const parks = [...new Set(plants.map((p) => (p.assigned_park || "").trim()).filter(Boolean))].sort();
    groups.push({
      key,
      dom: domKey(key),
      name: resolveName(plants[0]),
      plants,
      count: plants.length,
      cities,
      parks,
      allImmex: plants.every((p) => (p.slug || "").startsWith("immex-")),
      allCorridorVerified: plants.every((p) => p.corridor_verified === true),
      allPending: plants.every((p) => p.corridor_verified !== true),
      evidenceCount: plants.filter(
        (p) => p.capability_evidence_version || (Array.isArray(p.capability_evidence) && p.capability_evidence.length > 0)
      ).length,
      allLegacyVerified: plants.every((p) => p.verified === true && p.corridor_verified !== true),
      allDemo: plants.every((p) => p.is_demo === true),
      claimedCount: plants.filter((p) => p.claimed === true).length,
      reviewCount: plants.filter((p) => DUPLICATE_CANDIDATE_SLUGS.has((p.slug || "").toLowerCase())).length,
    });
  }
  return groups;
}

function plantMatches(m, q) {
  return [
    m.display_name, m.company_name, m.legal_name, m.legal_entity, m.slug,
    m.immex_program_reference, m.immex_number, m.plant_sequence_label,
    m.plant_address, m.street_address, m.address, m.colonia, m.postal_code,
    m.assigned_park, m.city, m.state,
  ].some((v) => (v || "").toString().toLowerCase().includes(q));
}

/* ── Per-plant badge row (row-level truth is never collapsed away) ──── */
function PlantRowBadges({ m, lang = "en" }) {
  const slug = (m.slug || "").toLowerCase();
  const isImmex = slug.startsWith("immex-");
  const isDup = DUPLICATE_CANDIDATE_SLUGS.has(slug);
  const evidenceBacked =
    !!m.capability_evidence_version ||
    (Array.isArray(m.capability_evidence) && m.capability_evidence.length > 0);
  return (
    <span className="inline-flex flex-wrap gap-1" data-testid={`plant-row-badges-${m.slug}`}>
      {isImmex ? <GovListedBadge lang={lang} testid={`plant-row-gov-${m.slug}`} /> : null}
      {m.corridor_verified === true ? <CorridorVerifiedBadge testid={`plant-row-corridor-${m.slug}`} /> : null}
      {m.corridor_verified !== true ? <PendingVerificationBadge lang={lang} testid={`plant-row-pending-${m.slug}`} /> : null}
      {evidenceBacked ? <EvidenceBackedBadge lang={lang} testid={`plant-row-evidence-${m.slug}`} /> : null}
      {m.verified === true && m.corridor_verified !== true ? <LegacyVerifiedBadge testid={`plant-row-legacy-${m.slug}`} /> : null}
      {m.claimed === true ? <ClaimedBadge testid={`plant-row-claimed-${m.slug}`} /> : (isImmex ? <UnclaimedBadge testid={`plant-row-unclaimed-${m.slug}`} /> : null)}
      {m.is_demo === true ? <DemoBadge testid={`plant-row-demo-${m.slug}`} /> : null}
      {isDup ? <ReviewCandidateBadge testid={`plant-row-review-${m.slug}`} /> : null}
    </span>
  );
}

/* ── One compact plant-location row ─────────────────────────────────── */
function PlantRow({ m, lang }) {
  const isDup = DUPLICATE_CANDIDATE_SLUGS.has((m.slug || "").toLowerCase());
  const address = m.plant_address || m.street_address || m.address || "";
  const postal = m.postal_code || m.zip_code || "";
  const immexRef = m.immex_program_reference || m.immex_number || "";
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2"
      style={{
        padding: "10px 14px 10px 18px",
        borderTop: "1px solid var(--line)",
        background: isDup ? "#fff7ed" : "transparent",
      }}
      data-testid={`plant-row-${m.slug}`}
    >
      <span
        className="font-mono text-xs"
        style={{
          padding: "2px 7px",
          background: "#f1f5f9",
          color: "var(--ink)",
          borderRadius: 2,
          letterSpacing: "0.04em",
          flexShrink: 0,
          minWidth: 36,
          textAlign: "center",
        }}
        data-testid={`plant-row-seq-${m.slug}`}
      >
        {m.plant_sequence_label || "—"}
      </span>

      <div className="flex-1 min-w-0 font-mono text-xs" style={{ minWidth: 220 }}>
        <div className="truncate" style={{ color: "var(--ink)" }} data-testid={`plant-row-address-${m.slug}`} title={address}>
          {address || (lang === "es" ? "Dirección no enviada" : "Address not submitted")}
          {postal ? <span style={{ color: "var(--ink-2)" }}> · {postal}</span> : null}
        </div>
        {m.assigned_park ? (
          <div className="truncate" style={{ color: "var(--ink-2)" }} data-testid={`plant-row-park-${m.slug}`}>
            <span className="overline">PARK</span> {m.assigned_park}
          </div>
        ) : null}
      </div>

      <span className="font-mono text-xs" style={{ color: "var(--ink)", flexShrink: 0 }} data-testid={`plant-row-immex-${m.slug}`}>
        <span className="overline" style={{ color: "var(--ink-2)" }}>IMMEX</span>{" "}
        {immexRef || "—"}
        {m.immex_program_year ? <span style={{ color: "var(--ink-2)" }}> · {m.immex_program_year}</span> : null}
      </span>

      <PlantRowBadges m={m} lang={lang} />

      <Link
        to={`/m/${m.slug}`}
        className="font-mono text-xs inline-flex items-center gap-1"
        style={{
          color: "var(--ink)",
          border: "1px solid var(--line)",
          padding: "5px 10px",
          borderRadius: 2,
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
        data-testid={`plant-row-link-${m.slug}`}
      >
        {lang === "es" ? "Ver perfil" : "View profile"} <ArrowRight size={11} strokeWidth={1.8} />
      </Link>
    </div>
  );
}

/* ── Company-level badges — shown ONLY when every plant qualifies ───── */
function CompanyBadges({ g, lang }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" data-testid={`company-badges-${g.dom}`}>
      {g.allImmex ? <GovListedBadge lang={lang} testid={`company-gov-${g.dom}`} /> : null}
      {g.allCorridorVerified ? <CorridorVerifiedBadge testid={`company-corridor-${g.dom}`} /> : null}
      {g.allPending ? <PendingVerificationBadge lang={lang} testid={`company-pending-${g.dom}`} /> : null}
      {g.evidenceCount > 0 ? (
        <span className="font-mono text-xs" style={{ color: "#115e59" }} data-testid={`company-evidence-note-${g.dom}`}>
          {g.evidenceCount === g.count
            ? (lang === "es" ? "Capacidades respaldadas por evidencia" : "Evidence-backed capabilities")
            : `${g.evidenceCount}/${g.count} ${lang === "es" ? "con evidencia" : "evidence-backed"}`}
        </span>
      ) : null}
      {g.allLegacyVerified ? <LegacyVerifiedBadge testid={`company-legacy-${g.dom}`} /> : null}
      {g.claimedCount === g.count ? (
        <ClaimedBadge testid={`company-claimed-${g.dom}`} />
      ) : g.claimedCount > 0 ? (
        <span className="font-mono text-xs" style={{ color: "#1e40af" }} data-testid={`company-claimed-partial-${g.dom}`}>
          {g.claimedCount}/{g.count} {lang === "es" ? "reclamadas" : "claimed"}
        </span>
      ) : g.allImmex ? (
        <UnclaimedBadge testid={`company-unclaimed-${g.dom}`} />
      ) : null}
      {g.allDemo ? <DemoBadge testid={`company-demo-${g.dom}`} /> : null}
      {g.reviewCount > 0 ? (
        <span className="font-mono text-xs" style={{ color: "#9a3412" }} data-testid={`company-review-note-${g.dom}`}>
          {g.reviewCount} {lang === "es"
            ? (g.reviewCount === 1 ? "candidato a revisión" : "candidatos a revisión")
            : (g.reviewCount === 1 ? "review candidate" : "review candidates")}
        </span>
      ) : null}
    </span>
  );
}

/* ── One company result (compact) with expandable plant rows ────────── */
function CompanyResult({ g, lang, open, onToggle, query }) {
  const matched = query ? g.plants.filter((p) => plantMatches(p, query)) : g.plants;
  const rows = query ? matched : g.plants;
  const cityLine = g.cities.slice(0, 2).join(" · ") + (g.cities.length > 2 ? ` +${g.cities.length - 2}` : "");
  const btnLabel = open
    ? (lang === "es" ? "Ocultar ubicaciones" : "Hide plant locations")
    : (lang === "es" ? "Ver ubicaciones de planta" : "View plant locations");
  return (
    <div className="cd-card" style={{ padding: 0, overflow: "hidden" }} data-testid={`company-card-${g.dom}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left"
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 16px", background: "transparent",
          border: 0, cursor: "pointer",
        }}
        data-testid={`company-toggle-${g.dom}`}
      >
        {open ? <ChevronDown size={16} strokeWidth={1.8} style={{ flexShrink: 0 }} /> : <ChevronRight size={16} strokeWidth={1.8} style={{ flexShrink: 0 }} />}
        <div className="flex-1 min-w-0">
          <div
            className="font-heading font-semibold truncate"
            style={{ fontSize: "clamp(15px, 1.2vw, 18px)", lineHeight: 1.25, color: "var(--ink)" }}
            data-testid={`company-name-${g.dom}`}
            title={g.name}
          >
            {g.name}
          </div>
          <div className="mt-1 font-mono text-xs flex flex-wrap items-center gap-x-3 gap-y-1" style={{ color: "var(--ink-2)" }}>
            <span className="inline-flex items-center gap-1">
              <MapPin size={10} strokeWidth={1.6} /> {cityLine || "—"}
            </span>
            <span className="inline-flex items-center gap-1" data-testid={`company-locations-count-${g.dom}`}>
              <Building2 size={10} strokeWidth={1.6} />
              {g.count} {lang === "es"
                ? (g.count === 1 ? "ubicación de planta" : "ubicaciones de planta")
                : (g.count === 1 ? "plant location" : "plant locations")}
            </span>
            {query && matched.length !== g.count ? (
              <span data-testid={`company-match-note-${g.dom}`}>
                {matched.length} {lang === "es" ? "de" : "of"} {g.count} {lang === "es" ? "coinciden" : "match"}
              </span>
            ) : null}
          </div>
          <div className="mt-2">
            <CompanyBadges g={g} lang={lang} />
          </div>
        </div>
        <span
          className="font-mono text-xs inline-flex items-center gap-1.5"
          style={{
            color: "var(--ink)",
            border: "1px solid var(--ink)",
            padding: "7px 12px",
            borderRadius: 2,
            flexShrink: 0,
            whiteSpace: "nowrap",
            background: open ? "var(--ink)" : "transparent",
          }}
          data-testid={`company-view-btn-${g.dom}`}
        >
          <span style={{ color: open ? "white" : "var(--ink)" }}>
            {btnLabel} ({g.count})
          </span>
        </span>
      </button>
      {open ? (
        <div data-testid={`company-plants-${g.dom}`}>
          {rows.map((p) => (
            <PlantRow key={p.slug || p.profile_id} m={p} lang={lang} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ── Control bar ────────────────────────────────────────────────────── */
function ControlBar({
  lang, q, setQ, view, setView, sort, setSort,
  companyCount, plantCount, onExpandAll, onCollapseAll,
}) {
  const SORTS = [
    ["name", lang === "es" ? "Nombre de empresa" : "Company name"],
    ["locations", lang === "es" ? "Nº de ubicaciones" : "Number of plant locations"],
    ["park", lang === "es" ? "Parque" : "Park"],
    ["sequence", lang === "es" ? "Secuencia de planta" : "Plant sequence"],
  ];
  return (
    <div
      className="cd-card"
      style={{ padding: "14px 16px" }}
      data-testid="directory-controlbar"
    >
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div
          className="flex items-center gap-2 bg-white flex-1"
          style={{ border: "1px solid var(--ink)", padding: "0 10px", minWidth: 240 }}
        >
          <Search size={15} strokeWidth={1.6} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "es"
              ? "Buscar empresa, IMMEX, P01, dirección o parque…"
              : "Search company, IMMEX ref, P01, address or park…"}
            className="flex-1 outline-none font-body"
            style={{ fontSize: 16, minHeight: 42, background: "transparent" }}
            data-testid="dir-search-input"
          />
          {q ? (
            <button type="button" onClick={() => setQ("")} aria-label="Clear search" style={{ minWidth: 28, minHeight: 28 }} data-testid="dir-search-clear">
              <X size={14} />
            </button>
          ) : null}
        </div>

        {/* Sort */}
        <label className="font-mono text-xs inline-flex items-center gap-2" style={{ color: "var(--ink-2)" }}>
          <span className="overline">{lang === "es" ? "Ordenar" : "Sort"}</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="cd-input font-mono text-xs"
            style={{ height: 36, maxWidth: 220 }}
            data-testid="dir-sort-select"
          >
            {SORTS.map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </label>

        {/* View toggle */}
        <div
          role="tablist"
          aria-label="View mode"
          style={{ display: "inline-flex", border: "1px solid var(--line)", borderRadius: 2, overflow: "hidden" }}
        >
          <button
            type="button"
            onClick={() => setView("grouped")}
            className="font-mono text-xs"
            style={{
              padding: "8px 14px", border: 0, cursor: "pointer",
              background: view === "grouped" ? "var(--ink)" : "transparent",
              color: view === "grouped" ? "white" : "var(--ink)",
            }}
            data-testid="dir-view-grouped"
          >
            {lang === "es" ? "Agrupado" : "Grouped"}
          </button>
          <button
            type="button"
            onClick={() => setView("flat")}
            className="font-mono text-xs"
            style={{
              padding: "8px 14px", border: 0, cursor: "pointer",
              background: view === "flat" ? "var(--ink)" : "transparent",
              color: view === "flat" ? "white" : "var(--ink)",
            }}
            data-testid="dir-view-flat"
          >
            {lang === "es" ? "Lista completa" : "Flat list"}
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="font-mono text-xs" style={{ color: "var(--ink)" }} data-testid="dir-result-count">
          <strong>{companyCount.toLocaleString()}</strong> {lang === "es" ? "empresas" : "companies"}
          <span style={{ color: "var(--ink-2)" }}> · </span>
          <strong>{plantCount.toLocaleString()}</strong> {lang === "es" ? "ubicaciones de planta" : "plant locations"}
        </div>
        {view === "grouped" ? (
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onExpandAll}
              className="font-mono text-xs"
              style={{ border: "1px solid var(--line)", padding: "5px 10px", borderRadius: 2, cursor: "pointer", background: "transparent" }}
              data-testid="dir-expand-all"
            >
              {lang === "es" ? "Expandir todo" : "Expand all"}
            </button>
            <button
              type="button"
              onClick={onCollapseAll}
              className="font-mono text-xs"
              style={{ border: "1px solid var(--line)", padding: "5px 10px", borderRadius: 2, cursor: "pointer", background: "transparent" }}
              data-testid="dir-collapse-all"
            >
              {lang === "es" ? "Contraer todo" : "Collapse all"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ── Main export ────────────────────────────────────────────────────── */
export function CompanyDirectory({ items, lang = "en" }) {
  const [q, setQ] = useState("");
  const [view, setView] = useState("grouped");
  const [sort, setSort] = useState("name");
  const [expanded, setExpanded] = useState(() => new Set());

  const query = q.trim().toLowerCase();
  const allGroups = useMemo(() => groupCompanies(items), [items]);

  const visibleGroups = useMemo(() => {
    let gs = query
      ? allGroups.filter((g) => g.plants.some((p) => plantMatches(p, query)))
      : allGroups;
    gs = [...gs];
    if (sort === "locations") {
      gs.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    } else if (sort === "park") {
      gs.sort((a, b) => {
        const pa = a.parks[0] || "\uffff";
        const pb = b.parks[0] || "\uffff";
        return pa.localeCompare(pb) || a.name.localeCompare(b.name);
      });
    } else {
      // "name" and "sequence" order groups alphabetically; plants inside a
      // group are ALWAYS in P01→Pnn order.
      gs.sort((a, b) => a.name.localeCompare(b.name));
    }
    return gs;
  }, [allGroups, query, sort]);

  const flatItems = useMemo(() => {
    let rows = query ? items.filter((p) => plantMatches(p, query)) : [...items];
    rows = [...rows];
    if (sort === "locations") {
      rows.sort((a, b) => (b.plant_count_for_legal_entity || 1) - (a.plant_count_for_legal_entity || 1) || resolveName(a).localeCompare(resolveName(b)));
    } else if (sort === "park") {
      rows.sort((a, b) => {
        const pa = (a.assigned_park || "\uffff");
        const pb = (b.assigned_park || "\uffff");
        return pa.localeCompare(pb) || resolveName(a).localeCompare(resolveName(b));
      });
    } else if (sort === "sequence") {
      rows.sort((a, b) => seqNum(a) - seqNum(b) || resolveName(a).localeCompare(resolveName(b)));
    } else {
      rows.sort((a, b) => resolveName(a).localeCompare(resolveName(b)) || seqNum(a) - seqNum(b));
    }
    return rows;
  }, [items, query, sort]);

  const shownPlantCount = query
    ? visibleGroups.reduce((n, g) => n + g.plants.filter((p) => plantMatches(p, query)).length, 0)
    : items.length;

  function toggle(key) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div data-testid="company-directory">
      <ControlBar
        lang={lang}
        q={q}
        setQ={setQ}
        view={view}
        setView={setView}
        sort={sort}
        setSort={setSort}
        companyCount={visibleGroups.length}
        plantCount={view === "grouped" ? shownPlantCount : flatItems.length}
        onExpandAll={() => setExpanded(new Set(visibleGroups.map((g) => g.key)))}
        onCollapseAll={() => setExpanded(new Set())}
      />

      {view === "flat" ? (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5" data-testid="dir-flat-grid">
          {flatItems.map((m) => (
            <PlantCard key={m.profile_id || m.slug} m={m} lang={lang} />
          ))}
        </div>
      ) : (
        <div className="mt-5 space-y-3" data-testid="dir-grouped-list">
          {visibleGroups.map((g) => (
            <CompanyResult
              key={g.key}
              g={g}
              lang={lang}
              query={query}
              open={query ? true : expanded.has(g.key)}
              onToggle={() => toggle(g.key)}
            />
          ))}
          {visibleGroups.length === 0 ? (
            <div className="cd-card p-10 text-center font-mono text-sm" style={{ color: "var(--ink-2)" }} data-testid="dir-no-results">
              {lang === "es" ? "Sin resultados para esta búsqueda." : "No results for this search."}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
